<script setup lang='ts'>
    import { useSlots } from 'vue';

    const emits = defineEmits([
        'onCancel',
        'onSave'
    ]);

    const props = defineProps({
        canCancel: {
            type: Boolean,
            default: true
        },
        maxWidth: {
            type: String,
            default: '700px'
        },
        width: {
            type: String,
            default: '50rem'
        },
        maxHeight: {
            // Optional override. When unset, no class is applied and Bulma's own
            // .modal-card max-height still governs, so existing consumers are unaffected.
            type: String,
            default: null
        },
        minHeight: {
            // Optional minimum height, for modals whose content changes, so the modal does not shrink to almost
            // nothing between views. It still grows with the content up to the max height, and the body scrolls beyond.
            type: String,
            default: null
        }
    });

    const slots = useSlots();
    const onClose = () => emits('onCancel');

    const onClickSubmit = () => emits('onSave');
</script>

<template>
    <b-modal
        has-modal-card
        model-value
        :can-cancel="props.canCancel"
        :width="props.maxWidth"
        @close="onClose">
        <div
            class="modal-card"
            :class="{ 'has-custom-max-height': props.maxHeight, 'has-custom-min-height': props.minHeight }">
            <header class="modal-card-head">
                <p class="modal-card-title">
                    <slot name="modal-header" />
                </p>
            </header>
            <section class="modal-card-body">
                <div>
                    <slot name="modal-body" />
                </div>
            </section>
            <footer
                v-if="slots['modal-footer']"
                class="modal-card-foot">
                <slot
                    name="modal-footer">
                    <b-field class="is-grouped-right">
                        <b-button
                            label="Cancel"
                            @click="onClose" />
                        <b-button
                            label="Add"
                            type="is-primary"
                            :disabled="false"
                            @click="onClickSubmit" />
                    </b-field>
                </slot>
            </footer>
            <footer
                v-else
                class="modal-card-foot empty" />
        </div>
    </b-modal>
</template>

<style scoped>
.modal-card-foot {
    /* Right-justify buttons to match .dialog button placement */
    justify-content: flex-end;
}

.empty {
    padding: 12px;
}

.modal-card {
     max-width: v-bind("props.maxWidth");
 }

.modal-card.has-custom-max-height {
    max-height: v-bind("props.maxHeight");
}

.modal-card.has-custom-min-height {
    min-height: v-bind("props.minHeight");
}

.modal {
    /* No way to only override the default value (50rem as of 2026-05-22) if the prop is set,
       using a default on the prop instead */
    --bulma-modal-content-width: v-bind("props.width");
}
</style>
